import React, { useState } from 'react';
import { Paperclip, Pencil, User } from 'lucide-react';
import { useCurrency } from '../../../Hooks/useCurrency';
import { useSelector } from 'react-redux';
import useIsMobile from '../../../Hooks/useIsMobile';
import { isSmartSellerMode } from '../../../Store/Reducers';
import { currentUserHasPermission, getProductTypeByName, sentimentEmoji, getFdbkScoreText } from '../../../Utils/Utilities';
import ContactActions from '../../../Components/Common/ContactActions';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../../Components/UI/tooltip';
import AttachmentPreviewModal from '../../../Components/Common/AttachmentPreviewModal';

const ThrDetailsInfo = ({
  threadData,
  threadTypeMeta,
  canShowAssignedTo = true,
  canShowReassignButton = true,
  onReassign
}) => {
  const currencySymbol = useCurrency();
  const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);
  const isMobile = useIsMobile();
  const thrViewTypeID = currentUserHasPermission('ThrViewTypeID');
  const canReassign = thrViewTypeID === 1 && canShowReassignButton;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const isCompletedSale = (Number(threadData?.typeID) === 2 || Number(threadData?.typeID) === 5) && Number(threadData?.statusID) === 2;

  // Handle file preview / download
  const handleFileDownload = (e) => {
    e && e.stopPropagation();
    const filePath = threadData?.thrFile || threadData?.ThrFile || threadData?.thrfile;
    if (filePath) {
      setIsPreviewOpen(true);
    }
  };

  // Build the main title (e.g., Inquiry #34464 - Tirth Infrastructure)
  const getDetailsTitle = () => {
    return `${threadTypeMeta?.nameSnSng || 'Thread'} Details`;
  };

  // Helper function to create the main badge data array
  const getBadgeData = () => {
    const badges = [];

    // Offering / Product
    if (threadData?.mainProduct) {
      badges.push({
        key: 'offering',
        value: `${threadData.mainProduct} ${threadData?.model ? `(${threadData.model})` : ''}`,
        className: "text-gray-900",
        label: `${getProductTypeByName(threadData.mainProduct)?.type ?? 'Product'}:`,
      });
    }

    // Quantity
    if (threadData?.mainQty && threadData.mainQty !== "Not specified" && threadData.mainQty !== "0" && threadData.mainQty !== 0) {
      badges.push({
        key: 'qty',
        value: threadData.mainQty,
        className: "text-gray-900",
        label: "Qty:",
      });
    }

    // Business Potential / Value
    if (threadData?.bizProspect > 0) {
      badges.push({
        key: 'value',
        label: "Value:",
        value: `${currencySymbol} ${threadData?.bizProspect ? threadData.bizProspect.toLocaleString() : "0"}`,
        className: "text-gray-900",
      });
    }

    const isCompletedOrHistory = Number(threadData?.statusID) === 2 || Number(threadData?.statusID) === 3;

    // Assigned / Credit To
    if (canShowAssignedTo && threadData?.pocUserName && threadData.pocUserName !== "-" && !isSmartSellerMode()) {
      badges.push({
        key: 'assigned',
        label: isCompletedOrHistory ? "Credit to:" : "Assigned to:",
        value: threadData.pocUserName,
        className: "text-gray-900",
      });
    }

    // Since / Received on date
    if (threadData?.sinceDTF) {
      badges.push({
        key: 'since',
        label: isCompletedOrHistory ? "Received on:" : "Since:",
        value: threadData.sinceDTF,
        className: isCompletedOrHistory ? (threadData?.sinceDTColor || "text-sahi-red font-semibold") : "text-gray-900",
      });
    }

    // Due date (only show Close by / Closed on if pending or if closed date is available)
    if (threadData?.dueDTF && threadData.dueDTF !== "Not set" && threadData.dueDTF !== "-") {
      badges.push({
        key: 'due',
        label: isCompletedOrHistory ? "Closed on:" : "Close by:",
        value: threadData.dueDTF,
        className: threadData?.dueDTColor || "text-gray-900",
      });
    }

    if (threadData?.reference && threadData.reference !== "-" && threadData.reference !== "0" && threadData.reference !== 0) {
      badges.push({
        key: 'ref',
        label: "Ref:",
        value: threadData.reference,
        className: "text-gray-900",
      });
    }

    if (threadData?.source && threadData.source !== "-") {
      badges.push({
        key: 'source',
        label: "Source:",
        value: `${threadData.source || '-'}`,
        className: "text-gray-900",
      });
    }

    // Attachment
    const attachedFile = threadData?.thrFile || threadData?.ThrFile || threadData?.thrfile;
    if (attachedFile) {
      const fileName = String(attachedFile);
      const isPdfOrDoc = fileName.toLowerCase().endsWith('.pdf') ||
        fileName.toLowerCase().endsWith('.doc') ||
        fileName.toLowerCase().endsWith('.docx') ||
        fileName.toLowerCase().endsWith('.xls') ||
        fileName.toLowerCase().endsWith('.xlsx') ||
        fileName.toLowerCase().endsWith('.txt');

      badges.push({
        key: 'file',
        label: "",
        value: isCompletedSale || isPdfOrDoc ? "Document" : "Screenshot",
        className: "text-sahi-blue cursor-pointer hover:underline",
        isFile: true,
      });
    }

    // Custom Fields
    if (threadData?.customFields && Array.isArray(threadData.customFields)) {
      let fieldDefinitions = {};
      if (thrExtraFields?.[0]?.Fields) {
        try {
          const parsedFields = JSON.parse(thrExtraFields[0].Fields);
          fieldDefinitions = parsedFields.reduce((acc, field) => {
            acc[field.FID] = field.F;
            return acc;
          }, {});
        } catch (error) {
        }
      }

      threadData.customFields.forEach((field, index) => {
        if (field?.V && field.V !== "-") {
          badges.push({
            key: `custom-${field.FID || index}`,
            label: fieldDefinitions[field.FID] ? `${fieldDefinitions[field.FID]}:` : "",
            value: field.V,
            className: "text-gray-900",
          });
        }
      });
    }

    return badges;
  };

  const connData = threadData?.connectionData || {};
  const contactDisplayName = connData.contactName || connData.bizName || "Unknown Contact";
  const hasContactInfo = Boolean(connData.contactName || connData.bizName || connData.mobile || connData.email);
  const isCompletedOrHistory = Number(threadData?.statusID) === 2 || Number(threadData?.statusID) === 3;

  return (
    <div className="space-y-3">
      {/* Contact Card above the order details - only for completed / history orders */}
      {isCompletedOrHistory && hasContactInfo && (
        <div className="bg-sahi-white rounded-lg p-3 border border-sahi-border-color flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 bg-sahi-blue-light/60 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-sahi-blue" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-color truncate" title={contactDisplayName}>
                {contactDisplayName}
              </p>
              {connData.bizName && connData.contactName && (
                <p className="text-xs text-sub-text-color truncate" title={connData.bizName}>
                  {connData.bizName}
                </p>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            <ContactActions
              connectionData={{
                connID: threadData?.connID || "",
                contactName: connData.contactName || "",
                bizName: connData.bizName || "",
                mobile: connData.mobile || "",
                email: connData.email || "",
                cc: connData.cc || "91"
              }}
              stageID={threadData?.currStageID}
              showDashes={false}
              showTemplate={true}
              connID={threadData?.connID}
            />
          </div>
        </div>
      )}

      {/* The main info badges section */}
      <div className="bg-sahi-white rounded-lg p-3 md:p-4">
        {/* Main info badges and notes - flex wrap layout */}
        <div className="flex flex-wrap gap-3 items-start">

          {/* Badges Container */}
          <div className="flex flex-wrap gap-2">
            {getBadgeData().map((badge) => {
              const isAssigned = badge.key === "assigned";
              const isFile = badge.isFile;

              return (
                <div
                  key={badge.key}
                  className={`inline-flex items-center gap-2 bg-sahi-disable px-2.5 md:px-3 py-1.5 md:py-2 rounded text-sm
                ${isFile || isAssigned ? "cursor-pointer hover:bg-gray-100 transition-colors" : ""}
                `}
                  onClick={(e) => {
                    if (isFile) {
                      handleFileDownload(e);
                      return;
                    }

                    if (isAssigned && onReassign) {
                      e.stopPropagation();
                      onReassign();
                    }
                  }}
                >
                  <div className="inline-flex items-center gap-1 min-w-0 flex-wrap">
                    {!badge.isFile && (
                      <span className="whitespace-nowrap text-gray-600">
                        {badge.label}
                      </span>
                    )}

                    <span
                      className={`font-semibold ${badge.className} ${badge.isFile ? "inline-flex items-center gap-1" : ""
                        } break-words`}
                    >
                      {badge.isFile ? (
                        <>
                          <Paperclip className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{badge.value}</span>
                        </>
                      ) : (
                        badge.value
                      )}
                    </span>

                    {canReassign && isAssigned && onReassign && (
                      <Pencil
                        className="h-4 w-4 ml-2 text-sahi-blue flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Notes, Feedback / Sentiment and Custom Fields */}
          {((threadData?.notes) || (threadData?.feedbackScore > 0) || (threadData?.feedbackNotes) || (threadData?.customFields && threadData.customFields.length > 0)) && (
            <div className="space-y-2 flex-1 min-w-[200px]">
              {threadData?.notes && (
                <div className="bg-sahi-disable px-2.5 md:px-3 py-1.5 md:py-2 rounded text-sm block w-full break-words">
                  <span className="text-gray-600">Notes: </span>
                  <span className="font-semibold text-gray-900">{threadData.notes}</span>
                </div>
              )}

              {threadData?.feedbackScore > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-sahi-disable px-2.5 md:px-3 py-1.5 md:py-2 rounded text-sm flex items-center gap-1.5 w-full break-words">
                        <span className="text-gray-600 flex-shrink-0">Sentiment:</span>
                        <span className="inline-flex items-center flex-shrink-0">
                          {sentimentEmoji(threadData.feedbackScore, '!h-4 !w-4')}
                        </span>
                        {threadData.failureReason && (
                          <span className="font-semibold text-gray-700 flex-shrink-0">
                            ({threadData.failureReason})
                          </span>
                        )}
                        {threadData.feedbackNotes && (
                          <span className="italic text-gray-900 truncate">
                            {threadData.feedbackNotes}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium mb-0.5">
                        Sentiment: {getFdbkScoreText(threadData.feedbackScore)}
                        {threadData.failureReason && ` - ${threadData.failureReason}`}
                      </p>
                      {threadData.feedbackNotes && <p>{threadData.feedbackNotes}</p>}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        filePath={threadData?.thrFile || threadData?.ThrFile || threadData?.thrfile}
      />
    </div>
  );
};

export default ThrDetailsInfo;